import React, { useEffect, useContext, useState } from 'react';
import { Alert, Grid, SpaceBetween } from '@amzn/awsui-components-react/polaris';
import { isReadOnlyModeEnabled } from 'src/services/FeatureFlagsService';
import { DashboardState, initialState } from 'src/models/dashboard/DashboardState';
import { dashboardReducer, ACTIONS } from 'src/services/dashboard/DashboardReducer';
import './Dashboard.scss';
import { GlobalAppContext } from 'src/components/App';
import useReducerWithLogger from 'src/services/utils/ReducerWithLogger';
import DashboardTiles from './DashboardTiles';
import { getTopOpenCalcReviewsSearchPayload } from './helpers/OpenCalculationReviewsSearchHelper';
import OpenApprovalsAndRequestsContainer from './OpenApprovalsAndRequestsContainer';
import { getTopOpenApprovalsSearchPayload } from './helpers/ApprovalsSearchHelper';
import { getTopOpenRequestsSearchPayload } from './helpers/RequestsSearchHelper';
import OpenCalculationReviewsContainer from './OpenCalculationReviewsContainer';
import TPEErrorWatcher from '../shared/TPEErrorWatcher';
import NotificationSummaryView from './NotificationSummaryView';
import { getTopOpenTaksSearchPayload } from './helpers/TasksSearchHelper';
import OpenTasksContainer from './OpenTasksContainer';
import TPEAction from 'src/models/common/TPEAction';
import CONSTANTS from 'src/utils/constants';
import MarkNotificationsComplete from '../shared/MarkNotificationsComplete';
import AccountingCalendarView from '../accounting-calendar/AccountingCalendarView';
import { deriveMecDayLabel } from '../accounting-calendar/mecDayLabel';
import { getFeatureFlags } from 'src/services/FeatureFlagsService';

export type ContextType = {
    dashboardState: DashboardState,
    dashboardDispatch: React.Dispatch<TPEAction>
}

const DashboardProvider = (props: any) => {
    const { state, dispatch, children } = props;
    const providerValue = React.useMemo(() => ({
        dashboardState: state, dashboardDispatch: dispatch
    }), [state, dispatch]);
    return (
        <DashboardContext.Provider value={providerValue}>
            {children}
        </DashboardContext.Provider>
    );
}

export const DashboardContext = React.createContext(null as unknown as ContextType);

export default function DashboardView() {
    const [state, dispatch] = useReducerWithLogger(dashboardReducer, initialState);
    const { userProfile, services } = useContext(GlobalAppContext);

    const { searchOpenCalcReviewsPayload, 
            searchOpenApprovalsPayload, 
            searchOpenRequestsPayload, 
            searchOpenTasksPayload,
            selectedNotifications } = state;

    const [ loadTime, setLoadTime ] = useState('');
    const [ triggerMarkComplete, setTriggerMarkComplete ] = useState('');

    const [openCalculationReviewsResult, openCalculationReviewsLoading, searchOpenCalcReviewsError] = services.dashboardService.searchOpenCalculationReviews(searchOpenCalcReviewsPayload);
    const [openApprovalsResult, openApprovalsLoading, searchOpenApprovalsError] = services.dashboardService.searchApprovals(searchOpenApprovalsPayload);
    const [openRequestsResult, openRequestsLoading, searchOpenRequestsError] = services.dashboardService.searchRequests(searchOpenRequestsPayload);
    const [openTasksResult, openTasksLoading, searchOpenTasksError] = services.dashboardService.searchTasks(searchOpenTasksPayload);
    const [notificationSummaryResult, notificationSummaryLoading, notificationSummaryError] = services.dashboardService.getNotificationSummary(loadTime);

    // Accounting calendar for the current year, used both by the embedded MEC calendar card
    // and to derive the dynamic "Day -8 of May MEC" label at the page top-right.
    const [calendarReloadKey, setCalendarReloadKey] = useState(0);
    const [calendarResult] = services.accountingCalendarService.getCalendar(undefined, calendarReloadKey);
    const mecDayLabel = deriveMecDayLabel(calendarResult);

    // Feature-flag gate (AWS AppConfig) for the embedded accounting-calendar card.
    const [accountingCalendarEnabled, setAccountingCalendarEnabled] = useState(false);
    useEffect(() => {
        getFeatureFlags()
            .then((flags) => setAccountingCalendarEnabled(flags.accountingCalendarEnabled))
            .catch((err) => console.error('Failed to load feature flags', err));
    }, []);

    const postProcessAfterMarkComplete = () => {
        dispatch(ACTIONS.SET_OPEN_TASKS_PAYLOAD.withPayload(getTopOpenTaksSearchPayload()));
        setLoadTime(new Date().toString());
    }

    useEffect(() => {
        setLoadTime(new Date().toString());
        dispatch(ACTIONS.SET_OPEN_CALC_REVIEWS_PAYLOAD.withPayload(getTopOpenCalcReviewsSearchPayload()));
        dispatch(ACTIONS.SET_OPEN_APPROVALS_PAYLOAD.withPayload(getTopOpenApprovalsSearchPayload()));
        dispatch(ACTIONS.SET_OPEN_REQUESTS_PAYLOAD.withPayload(getTopOpenRequestsSearchPayload()));
        dispatch(ACTIONS.SET_OPEN_TASKS_PAYLOAD.withPayload(getTopOpenTaksSearchPayload()));
    }, [])

    const readOnlyMode = isReadOnlyModeEnabled();

    return (
       <DashboardProvider state={state} dispatch={dispatch}>
            <div style={{ marginLeft: '50px', marginRight:'50px' }}>
                {readOnlyMode && (
                    <Alert type="warning" header="Read-only mode is active">
                        Edits are not allowed
                    </Alert>
                )}
                <div className="dashboardWelcomeRow">
                    <div className="welcomeUser">Welcome {userProfile?.firstName}!</div>
                    {accountingCalendarEnabled && mecDayLabel && <div className="mecDayHeaderLabel">{mecDayLabel}</div>}
                </div>
                <SpaceBetween direction="vertical" size="l">
                    {accountingCalendarEnabled ? (
                        <Grid gridDefinition={[{ colspan: { default: 12, s: 6 } }, { colspan: { default: 12, s: 6 } }]}>
                            <NotificationSummaryView accountingCalendarLayout {... {notificationSummaryResult, notificationSummaryLoading}}/>
                            <AccountingCalendarView services={services} />
                        </Grid>
                    ) : (
                        <NotificationSummaryView {... {notificationSummaryResult, notificationSummaryLoading}}/>
                    )}
                    <OpenApprovalsAndRequestsContainer {... {openApprovalsResult, openApprovalsLoading, openRequestsResult, openRequestsLoading}}/>
                    <OpenTasksContainer {... {openTasksResult, openTasksLoading, onMarkComplete: () => setTriggerMarkComplete(new Date().toString())}} />
                    <OpenCalculationReviewsContainer {... {openCalculationReviewsResult, openCalculationReviewsLoading}} />
                    <DashboardTiles />
                </SpaceBetween>
                <MarkNotificationsComplete triggerAction={triggerMarkComplete} selectedNotifications={selectedNotifications} postProcess={postProcessAfterMarkComplete}/>
                <TPEErrorWatcher services={services} errors={[searchOpenCalcReviewsError, searchOpenApprovalsError, 
                    searchOpenRequestsError, notificationSummaryError, searchOpenTasksError]} />
            </div>
        </DashboardProvider>
    );
}